import { z } from 'zod';
import { StageTargetDTO } from './FunnelStage';
import { StageType } from './Funnel';
import { ExtrapolationTypeSchema } from './FunnelSource';

export const basicFunnelMetricSchema = z.object({
  total: z.number(),
  new: z.number(),
  repeat: z.number(),
  unmatched: z.number(),
});
export type BasicFunnelMetric = z.infer<typeof basicFunnelMetricSchema>;

export const noMatchFunnelMetricSchema = basicFunnelMetricSchema.omit({
  unmatched: true,
});
export type NoMatchFunnelMetric = z.infer<typeof noMatchFunnelMetricSchema>;

export const basicExpectedMetricSchema = z.object({
  total: z.number(),
  new: z.number(),
  repeat: z.number(),
  expected: z.number(),
  unmatched: z.number(),
});
export type BasicExpectedMetric = z.infer<typeof basicExpectedMetricSchema>;

export const sourceRevenueNoMatchMetricSchema = basicExpectedMetricSchema.omit({
  unmatched: true,
});
export type SourceRevenueNoMatchMetric = z.infer<
  typeof sourceRevenueNoMatchMetricSchema
>;

export const matchRevenueMetricSchema = z.object({
  total: z.number(),
  attributed: z.number(),
  extrapolated: z.number(),
});

export type MatchRevenueMetric = z.infer<typeof matchRevenueMetricSchema>;

export const sourceMetricSchema = z.object({
  name: z.string(),
  desc: z.string(),
  total: z.object({
    revenue: basicExpectedMetricSchema.optional(),
    conversion: basicFunnelMetricSchema,
  }),
  attributed: z.object({
    revenue: sourceRevenueNoMatchMetricSchema.optional(),
    conversion: noMatchFunnelMetricSchema,
  }),
  extrapolated: z.object({
    revenue: basicExpectedMetricSchema.optional(),
    conversion: basicFunnelMetricSchema,
  }),
  matched: z.object({
    revenue: basicExpectedMetricSchema.optional(),
    conversion: basicFunnelMetricSchema,
  }),
  stageMatchRate: z.number().nullish(),
});
export type SourceMetric = z.infer<typeof sourceMetricSchema>;

export const totalSummaryMetricSchema = z.object({
  estConversion: z.number().optional(),
  estRevenue: z.number().optional(),
  revenue: z
    .object({
      total: basicExpectedMetricSchema,
    })
    .optional(),
  conversion: z.object({
    total: basicFunnelMetricSchema,
  }),
});

export const matchedSummaryMetricSchema = z.object({
  costPer: z.number(),
  estConversion: z.number().optional(),
  estRevenue: z.number().optional(),
  conversion: z.object({
    matched: basicFunnelMetricSchema,
    extrapolated: basicFunnelMetricSchema,
    attributed: noMatchFunnelMetricSchema,
  }),
  revenue: z
    .object({
      matched: basicExpectedMetricSchema,
      new: matchRevenueMetricSchema,
      repeat: matchRevenueMetricSchema,
      expected: matchRevenueMetricSchema,
      unmatched: z.object({
        total: z.number(),
        extrapolated: z.number(),
      }),
    })
    .optional(),
});

export const funnelStageMetricSchema = z.object({
  name: z.string(),
  desc: z.string(),
  sources: z.array(sourceMetricSchema),
  extrapolationType: ExtrapolationTypeSchema,
  total: totalSummaryMetricSchema,
  matched: matchedSummaryMetricSchema,
});

export type FunnelStageMetric = z.infer<typeof funnelStageMetricSchema>;

export const funnelStageGeneralSchema = z.object({
  phase: z.number(),
  adSpend: z.number(),
  reach: z.number(),
  frequency: z.number(),
  impressions: z.number(),
  pixelStatus: z.string(),
  lastPixelDate: z.string(),
  eventStatus: z.string(),
  lastEventDate: z.string(),
  firstEventTimestamp: z.number().nullable(),
  lastEventTimestamp: z.number().nullable(),
  uploadStatus: z.string(),
  lastUploadDate: z.string(),
  firstBenchmarkDate: z.number().nullable(),
  lastBenchmarkDate: z.number().nullable(),
  firstLeadTimestamp: z.number().nullable(),
  lastLeadTimestamp: z.number().nullable(),
  matchRate: z.number(),
});

export type FunnelStageGeneral = z.infer<typeof funnelStageGeneralSchema>;

export const funnelStagePerformanceSchema = z
  .object({
    totalInvest: z.number(),
    monthlyInvest: z.number(),
    monthlySpend: z.number(),
    sprintInvest: z.number(),
    sprintSpend: z.number(),
    averageSpend: z.number(),
    roi: z.number(),
    roas: z.number(),
    closePct: z.number(),
    revenue: z.number(),
    costPer: z.number(),
  })
  .merge(funnelStageGeneralSchema);
export type FunnelStagePerformance = z.infer<
  typeof funnelStagePerformanceSchema
>;

export const funnelStageStatsSchema = z.object({
  id: z.number(),
  name: z.string(),
  conversionName: z.string(),
  desc: z.string(),
  icon: z.string(),
  showTotal: z.boolean(),
  isKPI: z.boolean(),
  isSubKPI: z.boolean(),
  showOverview: z.boolean(),
  costPerMetric: z.number(),
  type: z.nativeEnum(StageType),
  stageTarget: StageTargetDTO.nullish(),
  dataSource: z
    .object({
      id: z.number(),
      name: z.string(),
      desc: z.string(),
      icon: z.string(),
    })
    .optional(),
  costPer: z.number(),
  spend: z.number(),
  estName: z.string().optional(),
  metrics: z.array(funnelStageMetricSchema),
  total: totalSummaryMetricSchema,
  matched: matchedSummaryMetricSchema,
});

export type FunnelStageStats = z.infer<typeof funnelStageStatsSchema>;

export const funnelGeneralResultSchema = z
  .object({
    stages: z.array(funnelStageStatsSchema),
  })
  .merge(funnelStagePerformanceSchema);
export type FunnelGeneralResult = z.infer<typeof funnelGeneralResultSchema>;
